import { useCallback, useMemo } from 'react';
import {
  assemblyOf, copyId, fittingChosen, fixedCam, levelTurn, gestureTurns, isTool, movementOf, nextId, nextIds, nextOf, nextPrepId, nextPrepIds, nextPrepOf, nextTurns, introTurns, prepId, toolLead, toolOutro, lastId, lastIds, lastLead, lastOf, lastTurns, prepIds, prepOf, prepTurns, useBook, type Movement, type Prep,
} from './assemblyStore';
import { fittingFor, useAssemblySlots, useDocTools, type Slot } from './assemblySlots';
import { poseAt, qRotate, quatFromEulerDeg } from './assemblyPose';
import { useCataloguedParts } from './photographedParts';
import { usePartModelJobs } from './partModels';
import { partDbSize } from './partIdentity';
import { useCatalogItems } from './catalogItems';
import { useMeshDims } from './meshDims';
import { builtBoxOf } from './openaiBlenderApi';
import { isTrueScale, madeOf, meshSrc, type Job3D } from './threeDApi';
import { pointedDriven, pointedDrillings, pointedStateAt } from './pointedStates';
import { useStepHoles } from './stepHolesRun';
import type { StepHole } from './stepHoles';
import { usePartsRun } from './partsRun';
import { namedIn, withLetter } from './partsCatalogue';
import { useLanguage } from '../../contexts/LanguageContext';
import { dimsUnlessBuilt, type Dims } from './meshAnnotations';
import type { ScenePart } from './assemblyScene';
import type { Assembly } from './assemblyPose';
import { pieceIdByKey, pieceItemId, subStepLabel, subStepOutputName, useStepsRun } from './stepsRun';
import { useAssemblySteps } from './categoryBands';
import { bakedFor, useSubstepOutputs } from './substepOutputs';
import { stillMadeOf } from './fittingModels';
import { toolRideOf } from './fromSet';
import { useManualSets } from './hardwareSets';

// A CLIP: one signed-off sub-step, packed small enough to travel.
//
// Phase 5 answers a movement and draws it from three stores at once — the manual's parts, the
// Parts DB's meshes, the fitting itself. None of those exist for a visitor with a phone and a
// QR code, so what gets published cannot be a reference into them: it has to be the movement
// and the meshes' addresses, written down. That is this file.
//
// IT IS NOT A VIDEO, and the distinction is the whole design. There are no frames to render,
// upload or serve: a clip is a dozen numbers and two or three glb urls, and every frame of it
// is computed by poseAt in the browser watching it — the same function, from the same numbers,
// as the viewer the fitting was signed off in. Which is why what you sign off is what plays.

// The one step a clip draws. The part being fitted onto sits at step 0 — already standing —
// so the whole of the transport belongs to the part that moves. See assemblyOf.
export const CLIP_STEP = 1;

// How long that step takes at 1×, and how fast it is actually played.
//
// SHARED WITH THE PHASE rather than chosen per player: a fitting signed off at this speed and
// published at another is not the clip anybody approved. Twelve turns in two seconds is a
// blur, which is a true fact about an M6 driving 12 mm and not something to fix by lying about
// the pitch — slowing the CLOCK shows the coupling, slowing the screw would hide it.
//
// FOUR SECONDS A SUB-STEP (2600 / 0.65): at 0.3 it was 8.7, long for a rail crossing 83 mm.
export const CLIP_MS = 2600;
export const CLIP_SPEED = 0.65;

// A SUB-STEP'S LENGTH AT 1×: the step's, once per time the gesture is made in turn — two barrel
// nuts turned in one after the other by one screwdriver take twice as long as one (see
// gestureTurns), rather than each getting half the time.
export const clipMs = (m: Movement) => CLIP_MS * gestureTurns(m);

// WHAT IS SAID UNDER A SUB-STEP AT `t`, and which of its sentences that is: Phase 4's beat for the
// slice being played (see SubStep.beatsEn) — the preparation's while it plays, the rest's after
// it, through every copy — with its place among the beats, "2/2". Counted by SENTENCES, not by
// slices: two copies driven with one sentence are one sentence. The whole sentence, counted as
// one, where there are no beats.
export function captionAt(beats: string[] | undefined, whole: string, m: Movement, t: number, named?: number[][]): { text: string; index: number; count: number } {
  // THE OPENING OF THE VERY FIRST SUB-STEP SAYS THE FIRST BEAT, and the rest is timed after it.
  if (m.opensWide) {
    const i = introTurns(m) / gestureTurns(m);
    return captionAt(beats, whole, { ...m, opensWide: false }, Math.max(0, (t - i) / (1 - i)), named);
  }
  // The slices the gesture takes, and not the tool's closing beat after them (see toolOutro): the
  // last beat is still said while the tool is put down.
  const all = gestureTurns(m);
  const turns = all - toolOutro(m);
  // THE MOVEMENT'S BEAT STILL SAID WHILE THE TOOL IS PUT DOWN, before the second hardware's (see
  // toolLead): the wood buttons' sentence came up over the screwdriver backing out.
  // AND THE SECOND HARDWARE'S STILL SAID while the tool is put down before the third (see lastLead).
  const opens = turns - nextTurns(m) - lastTurns(m);
  const held = (at: number, lead: number) => lead > 0 && t * all >= at && t * all < at + lead;
  const clock = held(opens, toolLead(m)) ? opens - 1e-6
    : held(turns - 1, lastTurns(m) ? lastLead(m) : 0) ? turns - 1 - 1e-6 : t * all;
  const slice = Math.min(turns - 1, Math.max(0, Math.floor(clock)));
  const prep = prepTurns(m);
  if (!beats?.length) return { text: whole, index: 0, count: 1 };
  // EACH BEAT WITH THE PART OF THE GESTURE IT NAMES, where every beat names one (see
  // Slot.beatItems): Phase 4 says a preparation first, and the gesture may play it later — 2-2's
  // spring washer goes onto the JCBC screws after the barrel nut is in.
  const byPart = beatsByPart(m, named, prep, turns);
  if (byPart && beats.length === named!.length) {
    const on = byPart.find((p) => slice >= p.from && slice < p.to) ?? byPart[byPart.length - 1];
    // Several beats on one part share its time; a part no beat names goes on with the last said.
    const said = byPart.filter((p) => p.from <= on.from && p.beats.length);
    const last = said[said.length - 1] ?? byPart.find((p) => p.beats.length)!;
    const k = last === on
      ? Math.min(on.beats.length - 1, Math.floor((clock - on.from) / (on.to - on.from) * on.beats.length))
      : last.from > on.from ? 0 : last.beats.length - 1;
    const beat = last.beats[Math.max(0, k)];
    // NUMBERED IN THE ORDER PLAYED, not the order Phase 4 said them: 2-2 counted 2/3, 1/3, 3/3.
    const index = byPart.flatMap((p) => p.beats).indexOf(beat);
    return { text: beats[beat] || whole, index, count: beats.length };
  }
  // MORE BEATS THAN THE ANIMATION HAS MOMENTS — its preparation not placed yet, still loading: the
  // beats in step with time all the same, never the whole sentence and then "1/2" once it lands.
  const even = Math.min(beats.length - 1, Math.max(0, Math.floor(Math.min(1, t * all / turns) * beats.length)));
  // A BEAT PER PART OF THE GESTURE where it has more than one: the preparation, the movement
  // through every copy, the second hardware (see Fitting.nextOn).
  const next = nextTurns(m);
  const parts = (prep ? 1 : 0) + 1 + (next ? 1 : 0);
  const part = slice < prep ? 0 : slice >= turns - next ? parts - 1 : prep ? 1 : 0;
  const index = prep || next ? (beats.length > parts ? even : Math.min(part, beats.length - 1))
    : beats.length > turns ? even : Math.min(slice, beats.length - 1);
  return { text: beats[index] || whole, index, count: beats.length };
}

// THE PARTS OF A GESTURE IN PLAYING ORDER, each its slices and the beats naming it: a beat goes to
// the first part whose own hardware it names, the prepared ones before the rest (a washer slid onto
// a screw names the screw too). Null where a beat names none of them.
function beatsByPart(m: Movement, named: number[][] | undefined, prep: number, turns: number) {
  if (!named?.length) return null;
  const next = nextOf(m);
  const nextPrep = nextPrepOf(m);
  // The third hardware (see Fitting.lastOn): a cover over the fastener names it too.
  const last = lastOf(m);
  const end = turns - lastTurns(m);
  const after = nextTurns(m) + lastTurns(m);
  const parts = [
    ...(prep ? [{ items: [prepOf(m)?.itemId], from: 0, to: prep, rank: 0 }] : []),
    { items: [m.movingId], from: prep, to: turns - after, rank: 3 },
    ...(nextPrep ? [{ items: [nextPrep.itemId], from: turns - after, to: turns - after + 1, rank: 1 }] : []),
    ...(next ? [{ items: [next.itemId], from: turns - after + (nextPrep ? 1 : 0), to: end, rank: 4 }] : []),
    ...(last ? [{ items: [last.itemId], from: end, to: turns, rank: 2 }] : []),
  ].map((p) => ({ ...p, beats: [] as number[] }));
  const ranked = [...parts].sort((a, b) => a.rank - b.rank);
  for (const [i, ids] of named.entries()) {
    const on = ranked.find((p) => p.items.some((id) => id != null && ids.includes(id)));
    if (!on) return null;
    on.beats.push(i);
  }
  return parts;
}

// One part of a clip, by the instance it plays: the run whose bytes are its mesh, whether that
// file already carries its real size, what the manual measured it at, and what to call it.
//
// THE JOB ID AND NOT A URL. The same clip is drawn from two places — this app, which reads a
// mesh through /api/notice/3d, and the public page, which reads it through a route gated on
// the tutorial being published — and an address baked in here would be the wrong one in one of
// them. See clipScene, which is where a clip meets whichever reader is drawing it.
export type ClipPart = {
  instanceId: string;
  job: number;
  trueScale: boolean;
  dims: Dims;
  // WHAT THE RUN MEASURED, for a part the manual never dimensioned — see mmScale, which asks
  // the drawing first and this last. Optional, because a clip baked before it existed has
  // neither the field nor a part that needed it: absent reads exactly as it always did.
  builtMm?: { x: number; y: number; z: number } | null;
  label: string;
  // WHAT IT IS MADE OF, where it is a piece an earlier sub-step left — the runs to download in
  // its place, and where each stands (see ScenePart.pieces). Absent on a part that is one mesh.
  pieces?: { job: number; m: number[]; name: string; at?: number; driven?: Driven[] }[];
  // The other hand of a pair: this run's mesh, reflected (see ScenePart.mirror).
  mirror?: boolean;
  // THE STATE OF ITS MESH THIS CLIP DRAWS, as an edit count, where not the part as it stands: before
  // the pointed screws' holes a later sub-step drives (see pointedStates). Absent: as it stands.
  at?: number;
  // AND THE STATES BEFORE IT, where this sub-step drives pointed screws into it (Louis 2026-10-30):
  // the state before each of their holes, in the order they are drilled, each drawn until `screw`
  // (an instance of the movement) is within `mm` of home — its head on the face, or its point, see
  // holeOpensAt. In the app only, like `at`. Absent: drawn at `at` the whole sub-step.
  driven?: Driven[];
};

export type Driven = { at: number; screw: string; mm: number };

// WHICH GESTURE THIS CLIP IS OF, as against which screen shows it.
//
// Several screens can be one gesture. "Slide the spring washer onto the JCBC screw" is done
// four times in step 1, and the four sub-steps are the same movement of the same two parts —
// Phase 5 answers it ONCE and the other three are marked as repeats of that answer (see
// Slot.repeat, and fittingFor, which is what resolves a repeat to the fitting that speaks for
// it). So four screens hold four clips that are the same picture four times.
//
// `gesture` is the leader's slot key, and it is the same string on all four. Anything drawing
// a list of screens can fold a run of them into one without re-deriving which gestures repeat
// — that question is answered where the slots are built, and answering it twice is how two
// readers of one manual come to disagree about how many gestures it has.
export type Clip = { movement: Movement; parts: ClipPart[]; gesture: string };

// WHICH OF THE MANUAL'S PARTS CAN BE DRAWN AT ALL: the ones with a curated mesh in the Parts
// DB, named the way this document names them, with the size the manual printed.
//
// A part without a mesh is left out rather than offered and then failing to appear — what is
// missing there is Phase 2's business. Its own hook because two callers need exactly the same
// list: the assembly phase's selectors, and the clips built below from what those selectors
// were set to. A part drawable in one and not the other is a fitting that cannot be published.
export function useDrawableParts(docId: string) {
  const { language } = useLanguage();
  const { parts } = useCataloguedParts(docId);
  const { modelOf: partModelOf, loaded: partsLoaded } = usePartModelJobs();
  // AND THE PIECES THE SUB-STEPS BEFORE HAVE MADE, each the mesh baked when its sub-step was signed
  // off (see useSubstepOutputs) — a part like any other to whatever draws a fitting, under its own
  // id (see pieceItemId). One whose sub-step is not signed off has no model, and is said so.
  const { steps } = useAssemblySteps(docId);
  const { subSteps } = useStepsRun(docId);
  const { outputs, failed } = useSubstepOutputs(docId);
  const pieces = useMemo(() => {
    // EVERY MODEL THAT STANDS FOR SOMETHING NOW, by run: each part's, and each sub-step's piece.
    // A piece listing a run that is not among them, or one whose shape has been edited since,
    // is no longer the piece that was signed off (see stillMadeOf) — and is not handed out.
    const live = new Map<number, Job3D>(
      [...partModelOf.values(), ...(outputs?.values() ?? [])].map((job) => [job.id, job]),
    );
    const made = pieceIdByKey(steps, subSteps);
    const bakedOf = (key: string): Job3D | undefined => {
      const output = made.get(key)?.output;
      return (outputs && output
        && bakedFor({ key, output: { en: output.en ?? '', fr: output.fr ?? '' } }, outputs)) || undefined;
    };
    return [...made].flatMap(([key, { id, output }]) => {
      const job = bakedOf(key);
      const itemId = pieceItemId(id);
      return job && itemId != null ? [{
        itemId,
        job,
        label: `${id} – ${subStepOutputName(output, language)}`,
        // Only once the Parts DB has answered: before that every part reads as gone.
        stale: partsLoaded && !stillMadeOf(job, live),
      }] : [];
    });
  }, [steps, subSteps, outputs, language, partModelOf, partsLoaded]);
  const modelOf = useMemo(
    () => new Map([
      ...partModelOf,
      ...pieces.filter((p) => !p.stale).map((p) => [p.itemId, p.job] as const),
    ]),
    [partModelOf, pieces],
  );
  // THE PIECES NO LONGER MADE OF WHAT THEY WERE SIGNED OFF ON, by item — a part of theirs has
  // another model now. They have no model here (above), and every sign-off standing on one is
  // withdrawn: see SignOffSync.
  const stalePieces = useMemo(
    () => new Set(pieces.filter((p) => p.stale).map((p) => p.itemId)),
    [pieces],
  );
  const loaded = partsLoaded && (outputs !== null || failed);
  // The database's sizes, for the parts this manual has not measured — see partDbSize.
  const { items: catalog } = useCatalogItems();

  // NAMED FIRST AND LETTERED SECOND — "Rondelle ressort (F)". The letter is how you find the
  // part in the bag and the name is how you recognise it in a list, and a column of letters
  // with the names pushed right is a column you read twice: once to scan, once to find.
  const choices = useMemo(
    () => [
      ...parts
        .filter((p) => partModelOf.has(p.itemId))
        .map((p) => ({
          itemId: p.itemId,
          label: withLetter(namedIn(p.part, language) || `#${p.itemId}`, p.part.letter),
          dims: partDbSize(catalog, p.itemId, p.part.size) as Dims,
        })),
      // No listed size: a baked piece is its own measure (see isTrueScale).
      ...pieces.filter((p) => !p.stale).map((p) => ({ itemId: p.itemId, label: p.label, dims: null as Dims })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parts, modelOf, language, catalog],
  );

  const byItem = useMemo(() => new Map(choices.map((c) => [c.itemId, c])), [choices]);
  // AND WHAT EACH PART'S MESH ACTUALLY MEASURES, beside what the manual printed about it.
  //
  // The two are different answers and the difference matters here. `dims` above is the size
  // the DRAWING gives — and a dimension drawing prints a rail's length and its height and no
  // thickness at all, which is a part with a missing axis. The mesh has three, always: it is a
  // shape and it has a bounding box (see useMeshDims, which measures it and anchors it in
  // millimetres through the printed size, exactly as every label in the app does).
  //
  // WHICH ONE IS AUTHORITATIVE DEPENDS ON THE QUESTION. For scaling the mesh into the scene it
  // is the drawing, because that is the anchor. For anything about the SHAPE — where its faces
  // are, where two parts touch — it is the mesh, because that is the thing on screen.
  const meshDims = useMeshDims(choices);
  return { choices, byItem, meshDims, modelOf, loaded, stalePieces };
}

// WHAT A PART IS PUT INTO MILLIMETRES BY, in the assembly's scene: the size its 3D model was built
// at, wherever the build measured one (see dimsUnlessBuilt). Its holes, its joint and where it stands against the others are all said in the
// frame it was built in, and a rail printed 260 mm and built 365 stopped 5 cm short of the frame it
// was placed against.
export const sceneDims = (job: Job3D, dims: Dims | null | undefined): Dims | null =>
  dimsUnlessBuilt(builtBoxOf(job), dims);

type ByItem = ReturnType<typeof useDrawableParts>['byItem'];
type ModelOf = ReturnType<typeof useDrawableParts>['modelOf'];
type Tools = ReturnType<typeof useDocTools>;

// The parts a fitting puts on screen, in the order the scene wants them. Null when a mesh it
// needs is missing — a fitting whose parts cannot both be drawn is not a clip, and publishing
// it as one would be an empty frame with a play button on it.
function clipParts(f: Movement, byItem: ByItem, modelOf: ModelOf, tools: Tools): ClipPart[] | null {
  const pick = (id: number | null, instanceId: string): ClipPart | null => {
    if (id == null) return null;
    const job = modelOf.get(id);
    const meta = byItem.get(id);
    if (!job) return null;
    return {
      instanceId,
      job: job.id,
      trueScale: isTrueScale(job),
      builtMm: builtBoxOf(job),
      dims: sceneDims(job, meta?.dims),
      label: meta?.label ?? `#${id}`,
      ...(madeOf(job) ? { pieces: madeOf(job)!.map(({ job: of, m, name }) => ({ job: of, m, name })) } : {}),
      ...(instanceId === 'moving' && f.mirrored ? { mirror: true } : {}),
    };
  };
  // BOTH PARTS, whatever the family. A placing drew one — on the reading that a gesture
  // fastening nothing has nothing in the picture but the part that moves, which was the
  // single-part kind's rule outliving the kind itself (see fittingChosen).
  const wanted = [pick(f.targetId, 'target'), pick(f.movingId, 'moving')];
  if (!wanted.every(Boolean)) return null;

  // AND THE TOOL, optional in a way the parts are not: a fitting without its two parts is not
  // a fitting and draws nothing, while a screwdriver the Parts DB has never seen — most allen
  // keys — leaves the rest of the picture exactly as it was.
  // AND THE SECOND, for the second hardware (see Fitting.nextToolId).
  const drawn: ClipPart[] = ([['tool', f.toolId], ['tool2', nextOf(f) ? f.nextToolId : null]] as const).flatMap(([instanceId, id]) => {
    const tool = tools.find((t) => t.value === id);
    const job = tool?.itemId != null ? modelOf.get(tool.itemId) : null;
    return job && tool ? [{
      instanceId,
      job: job.id,
      trueScale: isTrueScale(job),
      builtMm: builtBoxOf(job),
      dims: sceneDims(job, tool.dims as Dims),
      label: tool.label,
    }] : [];
  });
  // AND THE HARDWARE PREPARED ON THE MOVING PART IN THE HAND, once (see clipScene for the copies).
  const prep = prepOf(f);
  const washer = prep ? pick(prep.itemId, prepId(0)) : null;
  // AND THE SECOND HARDWARE, once (see Fitting.nextOn).
  const next = nextOf(f);
  const dowel = next ? pick(next.itemId, nextId(0)) : null;
  // AND THE HARDWARE PREPARED ON IT, once (see Fitting.nextPrep).
  const nextPrep = nextPrepOf(f);
  const ring = nextPrep ? pick(nextPrep.itemId, nextPrepId(0)) : null;
  // AND THE THIRD, once (see Fitting.lastOn).
  const last = lastOf(f);
  const cover = last ? pick(last.itemId, lastId(0)) : null;
  return [...(wanted as ClipPart[]), ...(washer ? [washer] : []), ...(dowel ? [dowel] : []), ...(ring ? [ring] : []), ...(cover ? [cover] : []), ...drawn];
}

// EVERY SIGNED-OFF SUB-STEP AS A CLIP, keyed the way the published page keys its screens —
// "1A", "2B": the step's printed number and the sub-step's letter.
//
// Keyed that way HERE and not at the two call sites, because both of them are about the
// published page: the review shows what a visitor will see, and the snapshot is what the
// visitor is sent. Phase 5 addresses a fitting by category and letter, which survives a
// re-analysis and means nothing to a page that was published from one; the slots are what
// translate between the two.
// A MOVEMENT WITH ITS TOOL WHERE A SET SAYS IT BEARS (see toolRideOf): what the phase draws and
// what a clip carries, so the published page draws the tool where the phase does.
export function useToolRide(docId: string) {
  const tools = useDocTools(docId);
  const { modelOf } = useDrawableParts(docId);
  const sets = useManualSets(docId);
  return useCallback(<F extends Movement>(f: F): F => {
    const rideOf = (toolId: string | null | undefined, on: Pick<Movement, 'toolSeat' | 'toolSpin'>) => {
      const tool = tools.find((t) => t.value === toolId);
      return (id: number | null | undefined) => (tool?.itemId != null
        ? toolRideOf(on, modelOf.get(id ?? -1), modelOf.get(tool.itemId), sets,
          (id) => [...modelOf.values()].find((j) => j.id === id)) : null);
    };
    // THE SECOND TOOL ON THE SECOND HARDWARE (see Fitting.nextToolId); no hand ever places it.
    const next = nextOf(f);
    const ride2 = next && f.nextToolId != null
      ? rideOf(f.nextToolId, { toolSeat: [0, 0, 0], toolSpin: [0, 0, 0] })(next.itemId) : null;
    const g = ride2 ? { ...f, nextToolRide: ride2 } : f;
    const rideOn = rideOf(f.toolId, f);
    const ride = rideOn(f.movingId);
    if (ride) return { ...g, toolRide: ride };
    // OR ON THE HARDWARE PREPARED ON IT, where the tool is for that (see Fitting.toolOnPrep); OR ON
    // THE SECOND HARDWARE (see Fitting.toolOnNext), where no second tool is for that.
    const onPrep = prepOf(f) ? rideOn(prepOf(f)!.itemId) : null;
    if (onPrep) return { ...g, toolRide: onPrep, toolOnPrep: true };
    const onNext = next && !ride2 ? rideOn(next.itemId) : null;
    return onNext ? { ...g, toolRide: onNext, toolOnNext: true } : g;
  }, [tools, modelOf, sets]);
}

// A PREPARATION PLAYED BY THE GESTURE THAT USES IT, and so not a screen of its own (see foldsOf):
// once the piece it makes can be drawn. Until it has been signed off its piece has no model, and
// it stays a sub-step to be answered and signed off like any other; that sign-off is what makes
// the piece the gesture after it stands on.
export const foldedAway = (slot: Slot, modelOf: Map<number, Job3D>): boolean => {
  const item = slot.foldsInto ? pieceItemId(slot.output?.id) : null;
  return item != null && modelOf.has(item);
};

// THE SUB-STEPS SHOWN AS SCREENS: every one, bar the preparations folded into their users.
export function useShownSlots(docId: string): Slot[] {
  const slots = useAssemblySlots(docId);
  const { modelOf } = useDrawableParts(docId);
  return useMemo(() => slots.filter((slot) => !foldedAway(slot, modelOf)), [slots, modelOf]);
}

// AND THE PREPARATIONS A GESTURE PLAYS FIRST (see Fitting.preps): for each folded into it, the
// mesh of its piece that came on last — the washer — and the way it came, read off the
// preparation's own signed-off fitting and turned into the frame the piece's meshes are listed
// in, which is the one that fitting was drawn in. A preparation whose piece is not one of the two
// parts here, or whose mesh is not in the piece, is played as nothing rather than guessed at.
export function usePreps(docId: string) {
  const book = useBook(docId);
  const slots = useAssemblySlots(docId);
  const { modelOf } = useDrawableParts(docId);
  return useCallback(<F extends Movement>(slot: Slot | null | undefined, f: F): F => {
    if (!slot?.preps.length) return f;
    const preps = slot.preps.flatMap((key): Prep[] => {
      const prep = slots.find((s) => s.key === key);
      if (!prep || !foldedAway(prep, modelOf)) return [];
      const item = pieceItemId(prep.output?.id);
      const on = item == null ? null : f.movingId === item ? 'moving' : f.targetId === item ? 'target' : null;
      if (!on || item == null) return [];
      const pf = fittingFor(book, prep);
      const came = modelOf.get(pf.movingId ?? Number.NaN)?.id;
      const index = madeOf(modelOf.get(item))?.findIndex((p) => p.job === came) ?? -1;
      if (index < 0) return [];
      return [{ on, index, axis: qRotate(quatFromEulerDeg(pf.targetSpin), pf.axis), travel: pf.travel }];
    });
    return preps.length ? { ...f, preps } : f;
  }, [book, slots, modelOf]);
}

// THE SUB-STEP A HOLE'S HARDWARE IS DRIVEN IN, as "3-2": the one it names (`at`, a letter of step
// `num`), or, where that one is no longer shown, the one sub-step of its step that drives its hardware
// (Louis 2026-10-30). Find holes names the sub-steps it was sent, and Phase 4 merging step 3's four
// screws into one left three of them named nowhere. The holes table and the clips read it alike.
// A HOLE'S HARDWARE AS THE ASSEMBLY NAMES IT: a hole's `via` is its place in the parts list, a
// fitting's parts are Parts DB rows (see useCataloguedParts). Null where it has no row.
export function useViaItem(docId: string) {
  const { items } = usePartsRun(docId);
  const { parts } = useCataloguedParts(docId);
  return useCallback((via: string | null) => {
    const row = via == null ? undefined : parts.find((p) => p.part === items[Number(via)]);
    return row ? String(row.itemId) : null;
  }, [items, parts]);
}

export function useHoleSubStep(docId: string) {
  const book = useBook(docId);
  const slots = useShownSlots(docId);
  const viaItem = useViaItem(docId);
  return useMemo(() => {
    const shown = new Set(slots.flatMap((s) => (s.step == null ? [] : [subStepLabel(s.step, s.letter)])));
    const drives = slots.flatMap((s) => {
      const f = s.step == null ? null : fittingFor(book, s);
      return f && fittingChosen(f) ? [{
        label: subStepLabel(s.step, s.letter),
        items: new Set(assemblyOf(movementOf(f)).instances.filter((i) => i.step === CLIP_STEP).map((i) => i.itemId)),
      }] : [];
    });
    return (num: number, at: string, via: string | null) => {
      const own = subStepLabel(num, at);
      const item = viaItem(via);
      if (shown.has(own) || item == null) return own;
      const by = drives.filter((d) => d.label.startsWith(`${num}-`) && d.items.has(item));
      return by.length === 1 ? by[0].label : own;
    };
  }, [book, slots, viaItem]);
}

// `unsigned`: EVERY ANSWERED SUB-STEP, signed off or not — the assembly phase's "All", which plays
// the manual through as it now stands. Never for what is published.
export function useClips(docId: string, unsigned = false): Record<string, Clip> {
  const book = useBook(docId);
  const slots = useShownSlots(docId);
  const tools = useDocTools(docId);
  const { byItem, modelOf } = useDrawableParts(docId);
  const withRide = useToolRide(docId);
  const withPreps = usePreps(docId);
  // THE POINTED SCREWS' HOLES, there once driven (see pointedStates).
  const holes = useStepHoles(docId);
  const { steps } = useAssemblySteps(docId);
  const { items } = usePartsRun(docId);
  const holeSubStep = useHoleSubStep(docId);
  const viaItem = useViaItem(docId);
  const labelOf = (num: number, hole: StepHole) => holeSubStep(num, hole.at, hole.via);

  return useMemo(() => {
    const out: Record<string, Clip> = {};
    const order = new Map(slots.flatMap((s, k) => (s.step == null ? [] : [[subStepLabel(s.step, s.letter), k] as const])));
    const byJob = new Map([...modelOf.values()].map((j) => [j.id, j]));
    const drillings = new Map<number, ReturnType<typeof pointedDrillings>>();
    const drillingsOf = (j: Job3D) => {
      if (!drillings.has(j.id)) drillings.set(j.id, pointedDrillings(j, holes.byStep, steps, items, labelOf));
      return drillings.get(j.id)!;
    };
    const stateOf = (job: number, label: string) => {
      const j = byJob.get(job);
      return j ? pointedStateAt(drillingsOf(j), j, label, order) ?? undefined : undefined;
    };
    // The k-th hole of a hardware in the part, to the k-th unit of it the movement drives (in the
    // order they go in), or its last unit where the movement drives fewer than the part has holes.
    const drivenOf = (job: number, label: string, screws: (via: string) => string[]) => {
      const j = byJob.get(job);
      const counted = new Map<string, number>();
      const out = (j ? pointedDriven(drillingsOf(j), j, label, order) : []).flatMap((d) => {
        const item = viaItem(d.via);
        const units = item == null ? [] : screws(item);
        const k = counted.get(d.via) ?? 0;
        counted.set(d.via, k + 1);
        return units.length ? [{ at: d.index, screw: units[Math.min(k, units.length - 1)], mm: d.mm }] : [];
      });
      return out.length ? { driven: out } : {};
    };
    for (const slot of slots) {
      if (slot.step == null) continue;
      // THE FITTING THAT SPEAKS FOR THIS SLOT — its own, or the one it repeats. Six sub-steps
      // that slide the same washer onto the same screw are one movement described once, and
      // each of them publishes it under its own name: a visitor reading 2A is shown the
      // gesture, not an empty frame with a note about where it was answered.
      const f = fittingFor(book, slot);
      // Signed off AND still answered, in that order and both required: validating is a click,
      // and a click on a slot whose parts were later cleared would leave a tick behind on
      // nothing.
      if ((!f.validated && !unsigned) || !fittingChosen(f)) continue;
      const found = clipParts(f, byItem, modelOf, tools);
      if (!found) continue;
      const label = subStepLabel(slot.step, slot.letter);
      const movement: Movement = {
        ...withPreps(slot, withRide(movementOf(f))),
        // Reflected, on a repeat on the other side (see Fitting.mirrorAll).
        ...(slot.repeat?.mirror && slot.repeat.leader !== slot.key ? { mirrorAll: true } : {}),
        // Opening on the whole, the very first (see Fitting.opensWide).
        ...(slot === slots.find((s) => s.step != null) ? { opensWide: true } : {}),
      };
      // The units of a hardware this movement drives (a Parts DB row, see useViaItem), in the order they go in.
      const driving = assemblyOf(movement).instances.filter((i) => i.step === CLIP_STEP);
      const screws = (item: string) => driving.filter((i) => i.itemId === item)
        .sort((a, b) => a.order - b.order).map((i) => i.id);
      const parts = found.map((p) => ({
        ...p,
        at: stateOf(p.job, label),
        ...drivenOf(p.job, label, screws),
        ...(p.pieces ? { pieces: p.pieces.map((q) => ({ ...q, at: stateOf(q.job, label), ...drivenOf(q.job, label, screws) })) } : {}),
      }));
      out[label] = { movement, parts, gesture: slot.repeat?.leader ?? slot.key };
    }
    return out;
    // `labelOf` is `holeSubStep`'s.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book, slots, tools, byItem, modelOf, withRide, withPreps, unsigned, holes.byStep, steps, items, holeSubStep, viaItem]);
}

// A CLIP MEETING ITS READER: the scene parts, with each mesh's address resolved by whoever is
// drawing it. Signed in as the app, that is /api/notice/3d/model/<job>; on the public page it
// is the route gated on the tutorial being published, and neither one belongs in the clip.
//
// THE MOVING PART ONCE PER COPY (see Fitting.copies): the clip names it once, and the movement
// says how many of it there are and where each goes.
// WHICH MESHES A GESTURE DRAWN IN THE MIRROR REFLECTS (see Fitting.mirrorAll): the gesture's own —
// what moves, its hardware, its tools — and NOT THE PART IT IS MADE ON. That part is what the
// sub-steps before have left, standing as it stands: reflected, 1-3's piece 102 had the left rail
// 1-1 fixed drawn on the right, on top of the right rail being prepared (Louis 2026-10-28). Its
// pose is still the reflected one, which for a part standing at the origin unturned is its own.
export const mirroredMesh = (instanceId: string) => instanceId !== 'target';

// A PART'S EARLIER STATE, drawn beside it and shown in its place until its screw is in (see
// ClipPart.driven): an instance of its own where the part is one mesh, a mesh of its own among its
// pieces where it is several.
const stateId = (id: string, k: number) => `${id}@${k}`;

// A SCENE PART AT THE STATES ITS SUB-STEP DRAWS IT AT (see ClipPart.at and .driven), by `urlAt`: its
// mesh, or each of its pieces, at `at`; then each driven state — beside it as an instance of its own
// where it is one mesh, after its pieces where it is several (after them all, so a piece's number is
// still its place in what the part is made of). The clips' and the assembly phase's alike.
export function withStates(part: ScenePart, p: ClipPart, urlAt: (job: number, at: number) => string): ScenePart[] {
  const pieces = p.pieces ?? [];
  if (part.pieces?.length) {
    return [{
      ...part,
      pieces: [
        ...part.pieces.map((s) => {
          const q = pieces.find((one) => one.name === s.name);
          return q?.at != null ? { ...s, url: urlAt(q.job, q.at) } : s;
        }),
        ...pieces.flatMap((q) => (q.driven ?? []).map((d, k) => ({ url: urlAt(q.job, d.at), m: q.m, name: stateId(q.name, k) }))),
      ],
    }];
  }
  const drawn = p.at != null ? { ...part, url: urlAt(p.job, p.at) } : part;
  return [drawn, ...(p.driven ?? []).map((d, k) => ({ ...drawn, instanceId: stateId(part.instanceId, k), url: urlAt(p.job, d.at) }))];
}

// `urlAt`: a mesh at an earlier state (see ClipPart.at) — the app's own, never a published page's,
// which draws every part as it stands. With it, the states a sub-step drives through too.
export const clipScene = (
  clip: Clip, url: (job: number) => string = meshSrc, urlAt?: (job: number, at: number) => string,
): ScenePart[] =>
  clip.parts.flatMap((p) => {
    const base: ScenePart = {
      instanceId: p.instanceId,
      url: url(p.job),
      ...(p.pieces?.length ? { pieces: p.pieces.map((q) => ({ url: url(q.job), m: q.m, name: q.name })) } : {}),
      // The hand reflected, and the gesture's meshes on a gesture drawn in the mirror (see mirroredMesh).
      ...(!!p.mirror !== (!!clip.movement.mirrorAll && mirroredMesh(p.instanceId)) ? { mirror: true } : {}),
      dims: p.dims,
      trueScale: p.trueScale,
      builtMm: p.builtMm ?? null,
      label: p.label,
    };
    const [part, ...states] = urlAt ? withStates(base, p, urlAt) : [base];
    // The moving part, and the hardware prepared on it, once per copy.
    if (p.instanceId === prepId(0)) return prepIds(clip.movement).map((id) => ({ ...part, instanceId: id }));
    if (p.instanceId === nextId(0)) return nextIds(clip.movement).map((id) => ({ ...part, instanceId: id }));
    if (p.instanceId === nextPrepId(0)) return nextPrepIds(clip.movement).map((id) => ({ ...part, instanceId: id }));
    if (p.instanceId === lastId(0)) return lastIds(clip.movement).map((id) => ({ ...part, instanceId: id }));
    if (p.instanceId !== 'moving') return [part, ...states];
    return [part, ...states, ...(clip.movement.copies ?? []).map((_, n) => ({ ...part, instanceId: copyId(n + 1) }))];
  });

// AND EACH STATE ONE MESH IS DRAWN AT BESIDE IT, standing where its part stands (see withStates).
export function withStateInstances(asm: Assembly, parts: ClipPart[]): Assembly {
  const clones = parts.flatMap((p) => {
    const inst = asm.instances.find((i) => i.id === p.instanceId);
    return inst && !p.pieces?.length ? (p.driven ?? []).map((_, k) => ({ ...inst, id: stateId(p.instanceId, k) })) : [];
  });
  return clones.length ? { ...asm, instances: [...asm.instances, ...clones] } : asm;
}

// `states`: drawn with the states its sub-step drives through (see clipScene's `urlAt`).
export type ClipAssembly = Assembly & { states: boolean };
export const clipAssembly = (clip: Clip, states = false): ClipAssembly => {
  const asm = assemblyOf(clip.movement);
  return { ...(states ? withStateInstances(asm, clip.parts) : asm), states };
};

// WHICH STATE EACH PART IS DRAWN AT, at moment `t` (see ClipPart.driven): as many of its driven holes
// as have their screw within reach of home, in the order drilled — the states and the part itself
// that are not that one hidden, and with `hidden` (parts hidden by hand) every state of each. Null
// where nothing in these parts changes.
export function statesAt(parts: ClipPart[], asm: Assembly, t: number, hidden: string[] = []) {
  if (!parts.some((p) => p.driven?.length || p.pieces?.some((q) => q.driven?.length))) return null;
  const left = new Map(poseAt(asm, CLIP_STEP, t).map((p) => [p.instance.id, p.left]));
  const reached = (driven: Driven[]) => {
    let k = 0;
    while (k < driven.length && (left.get(driven[k].screw) ?? Infinity) <= driven[k].mm + 1e-6) k += 1;
    return k;
  };
  // Of a mesh drawn at `id` with its states beside it, the ones not drawn.
  const off = (id: string, driven: Driven[], all = false) => {
    const k = all ? -1 : reached(driven);
    return [...(k < driven.length ? [id] : []), ...driven.map((_, j) => stateId(id, j)).filter((_, j) => j !== k)];
  };
  return {
    hide: [...new Set([...hidden, ...parts.flatMap((p) => (p.driven?.length && !p.pieces?.length
      ? off(p.instanceId, p.driven, hidden.includes(p.instanceId)) : []))])],
    pieces: parts.flatMap((p) => (p.pieces?.some((q) => q.driven?.length)
      ? [[p.instanceId, p.pieces.flatMap((q) => (q.driven?.length ? off(q.name, q.driven) : []))] as const] : [])),
  };
}

// The same, for a clip drawn with its states.
export const clipStatesAt = (clip: Clip, asm: ClipAssembly, t: number) =>
  (asm.states ? statesAt(clip.parts, asm, t) : null);

// THE PRODUCT, ASSEMBLED — what the last gesture leaves, standing still: the screen a tutorial
// ends on, and the tile the assembly phase ends on.
//
// MADE FROM THE LAST CLIP AND NOT STORED BESIDE IT. Its two parts at rest ARE the product, so the
// same clip with nowhere to travel is the picture: nothing more to publish, nothing a re-publish
// could leave out of step with the gesture before it. No tool — nobody is fastening anything — and
// not that gesture's shot, which is close in on a screw: the whole of it, a quarter turn off
// square so it reads as a solid.
const FINISHED_TURN: [number, number, number] = [0, 25, 0];
export const finishedClip = (last: Clip): Clip => ({
  movement: {
    ...last.movement,
    travel: 0, pitch: 0, depth: 0, turn: null, drive: 'straight', toolId: null,
    copies: last.movement.copies?.map((c) => ({ ...c, travel: 0 })),
    ...fixedCam(), camStartRot: [...FINISHED_TURN], camEndRot: [...FINISHED_TURN],
  },
  parts: last.parts.filter((p) => !isTool(p.instanceId)),
  gesture: 'finished',
});

// THE SAME PRODUCT AS THE PUBLISHED TUTORIAL'S COVER: further round and closer in, so it fills a
// portrait frame the way a product photograph does (Louis 2026-10-23).
const COVER_TURN = levelTurn(15, 40);
const COVER_ZOOM = 1.0;
export const coverClip = (finished: Clip): Clip => ({
  ...finished,
  movement: {
    ...finished.movement,
    camStartRot: [...COVER_TURN], camEndRot: [...COVER_TURN],
    camStartZoom: COVER_ZOOM, camEndZoom: COVER_ZOOM,
  },
});

// EVERY RUN A CLIP DOWNLOADS — a piece's parts in the piece's place, which is what clipScene
// asks for. For whoever fetches a clip's meshes ahead of drawing it.
export const clipJobs = (clip: Clip): number[] =>
  clip.parts.flatMap((p) => (p.pieces?.length ? p.pieces.map((q) => q.job) : [p.job]));
